import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AccountPreferencesResult, type ApiError, type ContactPreferencesResult } from '@whinerp/client';
import { Button, Field, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Customer preferences (G-036), on the company and on the person.
 *
 * Each value says where it came from - their own, their company's, or the
 * workspace default - because "why is this due in 30 days when they are on
 * 60?" is answered by the source, not the number.
 *
 * A stated language the product cannot write documents in is shown with a
 * line saying which language documents go out in instead. Accepting "French"
 * and silently sending English is the failure this avoids.
 *
 * The channel field is labelled as a preference and nothing more. It is not
 * consent, and the panel says so beside it.
 */

type Effective = AccountPreferencesResult['effective'];
type Channel = NonNullable<Effective['channel']['value']>;
type Delivery = Effective['documentDelivery']['value'];
const CHANNELS: Channel[] = ['email', 'phone', 'sms', 'whatsapp', 'post', 'in-person'];
const DELIVERIES: Delivery[] = ['email', 'portal', 'post'];

const selectClass = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

function SourceNote({ source }: { source: Effective['channel']['source'] }): JSX.Element | null {
  const t = useT();
  if (source === 'none') return null;
  return (
    <span className="text-meta text-ink-meta" data-preference-source={source}>
      {t(`customerPrefs.source.${source}`)}
    </span>
  );
}

function LanguageNotice({ effective }: { effective: Effective }): JSX.Element | null {
  const t = useT();
  if (effective.documentLanguage.honoured) return null;
  return (
    <p className="text-meta text-warning-text" data-preference-language-not-honoured>
      {t('customerPrefs.language.notHonoured', {
        values: {
          stated: effective.language.value ?? '',
          used: t(`customerPrefs.language.${effective.documentLanguage.value}`),
        },
      })}
    </p>
  );
}

export function AccountPreferencesPanel({ accountId }: { accountId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-028');
  const key = ['account-preferences', accountId];
  const current = useQuery<AccountPreferencesResult, ApiError>({
    queryKey: key,
    queryFn: () => api.accountPreferences({ path: { accountId } }),
    enabled: enabled.enabled,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    paymentTermsDays: '',
    preferredLanguage: '',
    preferredChannel: '' as Channel | '',
    documentDelivery: '' as Delivery | '',
    deliveryInstructions: '',
  });
  useEffect(() => {
    const saved = current.data?.preferences;
    if (!saved) return;
    setDraft({
      paymentTermsDays: saved.paymentTermsDays === null ? '' : String(saved.paymentTermsDays),
      preferredLanguage: saved.preferredLanguage ?? '',
      preferredChannel: saved.preferredChannel ?? '',
      documentDelivery: saved.documentDelivery ?? '',
      deliveryInstructions: saved.deliveryInstructions ?? '',
    });
  }, [current.data]);

  const save = useMutation({
    mutationFn: () =>
      api.saveAccountPreferences({
        path: { accountId },
        body: {
          paymentTermsDays: draft.paymentTermsDays.trim() === '' ? null : Number(draft.paymentTermsDays),
          preferredLanguage: draft.preferredLanguage.trim() || null,
          preferredChannel: draft.preferredChannel || null,
          documentDelivery: draft.documentDelivery || null,
          deliveryInstructions: draft.deliveryInstructions.trim() || null,
        },
      }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  if (!enabled.enabled || current.isPending) return null;
  if (current.isError) {
    return (
      <Panel data-region="account-preferences">
        <p className="text-dense text-danger-text">{errorText(current.error)}</p>
      </Panel>
    );
  }
  const effective = current.data.effective;

  return (
    <Panel data-region="account-preferences">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <SectionLabel>{t('customerPrefs.title')}</SectionLabel>
          {!editing ? (
            <Button intent="secondary" size="small" data-preferences-edit onClick={() => setEditing(true)}>
              {t('customerPrefs.edit')}
            </Button>
          ) : null}
        </div>

        {!editing ? (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-dense sm:grid-cols-2">
            <dt className="text-ink-meta">{t('customerPrefs.terms')}</dt>
            <dd className="flex flex-wrap gap-2" data-preference="terms">
              <span className="text-ink">
                {t('customerPrefs.terms.days', { values: { days: String(effective.paymentTermsDays.value) } })}
              </span>
              <SourceNote source={effective.paymentTermsDays.source} />
            </dd>

            <dt className="text-ink-meta">{t('customerPrefs.language')}</dt>
            <dd className="flex flex-col" data-preference="language">
              <span className="flex flex-wrap gap-2">
                <span className="text-ink">{effective.language.value ?? t('customerPrefs.notSaid')}</span>
                <SourceNote source={effective.language.source} />
              </span>
              <LanguageNotice effective={effective} />
            </dd>

            <dt className="text-ink-meta">{t('customerPrefs.channel')}</dt>
            <dd className="flex flex-col" data-preference="channel">
              <span className="text-ink">
                {effective.channel.value ? t(`customerPrefs.channel.${effective.channel.value}`) : t('customerPrefs.notSaid')}
              </span>
              {/* Said beside the field, because it is the confusion that matters. */}
              <span className="text-meta text-ink-meta">{t('customerPrefs.channel.notConsent')}</span>
            </dd>

            <dt className="text-ink-meta">{t('customerPrefs.delivery')}</dt>
            <dd className="flex flex-wrap gap-2" data-preference="delivery">
              <span className="text-ink">{t(`customerPrefs.delivery.${effective.documentDelivery.value}`)}</span>
              <SourceNote source={effective.documentDelivery.source} />
            </dd>

            {effective.deliveryInstructions ? (
              <>
                <dt className="text-ink-meta">{t('customerPrefs.instructions')}</dt>
                <dd className="whitespace-pre-line text-ink">{effective.deliveryInstructions}</dd>
              </>
            ) : null}
          </dl>
        ) : (
          <form
            data-preferences-form
            className="flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <Field label={t('customerPrefs.terms.field')} type="number" min={0} max={365} data-preference-field="terms"
              placeholder={t('customerPrefs.terms.default')} value={draft.paymentTermsDays}
              onChange={(e) => setDraft({ ...draft, paymentTermsDays: e.currentTarget.value })} />
            <Field label={t('customerPrefs.language.field')} maxLength={2} placeholder="ar" data-preference-field="language"
              value={draft.preferredLanguage}
              onChange={(e) => setDraft({ ...draft, preferredLanguage: e.currentTarget.value })} />
            <label className="flex flex-col gap-1 text-meta text-ink-meta">
              {t('customerPrefs.channel')}
              <select className={selectClass} data-preference-field="channel" value={draft.preferredChannel}
                onChange={(event) => setDraft({ ...draft, preferredChannel: event.target.value as Channel | '' })}>
                <option value="">{t('customerPrefs.notSaid')}</option>
                {CHANNELS.map((channel) => (
                  <option key={channel} value={channel}>{t(`customerPrefs.channel.${channel}`)}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-meta text-ink-meta">
              {t('customerPrefs.delivery')}
              <select className={selectClass} data-preference-field="delivery" value={draft.documentDelivery}
                onChange={(event) => setDraft({ ...draft, documentDelivery: event.target.value as Delivery | '' })}>
                <option value="">{t('customerPrefs.source.workspace')}</option>
                {DELIVERIES.map((delivery) => (
                  <option key={delivery} value={delivery}>{t(`customerPrefs.delivery.${delivery}`)}</option>
                ))}
              </select>
            </label>
            <Field label={t('customerPrefs.instructions')} data-preference-field="instructions"
              value={draft.deliveryInstructions}
              onChange={(e) => setDraft({ ...draft, deliveryInstructions: e.currentTarget.value })} />
            {save.error ? <p className="text-dense text-danger-text">{errorText(save.error as ApiError)}</p> : null}
            <div className="flex gap-2">
              <Button intent="primary" size="small" type="submit" data-preferences-save disabled={save.isPending}>
                {t('customerPrefs.save')}
              </Button>
              <Button intent="secondary" size="small" type="button" onClick={() => setEditing(false)}>
                {t('customerPrefs.cancel')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Panel>
  );
}

/** The person's own language and channel, with what their company's fills in. */
export function ContactPreferencesPanel({ contactId }: { contactId: string }): JSX.Element | null {
  const t = useT();
  const enabled = useEntitlement('F-028');
  const current = useQuery<ContactPreferencesResult, ApiError>({
    queryKey: ['contact-preferences', contactId],
    queryFn: () => api.contactPreferences({ path: { contactId } }),
    enabled: enabled.enabled,
  });
  if (!enabled.enabled || !current.data) return null;
  const effective = current.data.effective;
  return (
    <Panel data-region="contact-preferences">
      <div className="flex flex-col gap-1 text-dense">
        <SectionLabel>{t('customerPrefs.title')}</SectionLabel>
        <span className="flex flex-wrap gap-2" data-preference="language">
          <span className="text-ink-meta">{t('customerPrefs.language')}:</span>
          <span className="text-ink">{effective.language.value ?? t('customerPrefs.notSaid')}</span>
          <SourceNote source={effective.language.source} />
        </span>
        <LanguageNotice effective={effective} />
        <span className="flex flex-wrap gap-2" data-preference="channel">
          <span className="text-ink-meta">{t('customerPrefs.channel')}:</span>
          <span className="text-ink">
            {effective.channel.value ? t(`customerPrefs.channel.${effective.channel.value}`) : t('customerPrefs.notSaid')}
          </span>
          <SourceNote source={effective.channel.source} />
        </span>
        <span className="text-meta text-ink-meta">{t('customerPrefs.channel.notConsent')}</span>
      </div>
    </Panel>
  );
}
